import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
  Textarea,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { inactiveReasonOptions, type InactiveReason } from '../../../types';

export interface MarkInactiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statusMutation: UseMutationResult<unknown, Error, { status: string; notes?: string }>;
}

export function MarkInactiveDialog({
  open,
  onOpenChange,
  statusMutation,
}: MarkInactiveDialogProps) {
  const [inactiveReason, setInactiveReason] = useState<InactiveReason | ''>('');
  const [inactiveNotes, setInactiveNotes] = useState('');

  const handleCancel = () => {
    onOpenChange(false);
    setInactiveReason('');
    setInactiveNotes('');
  };

  const handleSubmit = () => {
    const reason = inactiveReasonOptions.find(o => o.value === inactiveReason)?.label || 'Other';
    const notes = inactiveNotes ? `${reason}: ${inactiveNotes}` : reason;
    statusMutation.mutate(
      { status: 'inactive', notes },
      {
        onSuccess: () => {
          onOpenChange(false);
          setInactiveReason('');
          setInactiveNotes('');
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Mark Return as Inactive</DialogTitle>
          <DialogContent>
            <p style={{ marginBottom: '16px', color: 'var(--tl-text-secondary)' }}>
              This will mark the tax return as inactive. Inactive returns are hidden by default in the list view.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Reason" required>
                <Dropdown
                  placeholder="Select a reason"
                  value={inactiveReason ? inactiveReasonOptions.find(o => o.value === inactiveReason)?.label : ''}
                  selectedOptions={inactiveReason ? [inactiveReason] : []}
                  onOptionSelect={(_, data) => setInactiveReason(data.optionValue as InactiveReason)}
                  style={{ width: '100%' }}
                >
                  {inactiveReasonOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Additional Notes">
                <Textarea
                  value={inactiveNotes}
                  onChange={(_, data) => setInactiveNotes(data.value)}
                  placeholder="Add any additional details..."
                  rows={3}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!inactiveReason || statusMutation.isPending}
              onClick={handleSubmit}
            >
              {statusMutation.isPending ? 'Marking...' : 'Mark Inactive'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
